<template>
    <v-form ref="form" v-model="valid" data-cy="guest-category-form">
        <v-row class="mb-4 flex-wrap" dense>
            <v-col>
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col class="align-center">
                <v-row dense>
                    <v-switch
                        v-model="catalogue.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4 width-max-content"
                        :label="catalogue.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <TextFieldLocale
                    v-model="catalogue.name[activeLangCode]"
                    :label="`${$t('name')}*`"
                    :active-lang-code="activeLangCode"
                    data-cy="name"
                    :rules="[requiredRule('name')]"
                />
            </v-col>
        </v-row>
        <v-row v-if="useIsShowVehicleTypes" dense>
            <v-col>
                <v-autocomplete
                    v-model="catalogue.vehicleTypeId"
                    :items="vehicleTypesList"
                    :placeholder="$t('vehicle_type')"
                    item-title="name"
                    item-value="id"
                    single-line
                    density="compact"
                    clearable
                />
            </v-col>
        </v-row>
        <v-row v-if="!hideButtons" no-gutters class="gc-4">
            <v-btn class="save-btn" data-cy="save" :disabled="isLoading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn class="cancel-btn" data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import type {InfoCatalogueType} from '~/utils/types/account/products/types'
    import {initInfoCatalogue} from '~/utils/types/account/products/helpers'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {contractServiceType} from '~/utils/types/types'

    const props = defineProps<{
        editCatalogueId?: number | null
        contractId: number
        hideButtons?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const catalogue = ref<InfoCatalogueType>(initInfoCatalogue())
    const vehicleTypesList = ref([])
    const appMethod = ref('ownProductServiceCategories')

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        switch (supplierStore.contract.serviceType) {
            case contractServiceType.ownTransfer:
                appMethod.value = 'ownTransferServiceCategories'
                break
            case contractServiceType.ownExcursion:
                appMethod.value = 'ownExcursionServiceCategories'
                break
            default:
                appMethod.value = 'ownProductServiceCategories'
        }

        if (props.editCatalogueId) await loadData()
        if (useIsShowVehicleTypes.value) {
            await loadVehicleTypes()
        }
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const {categories} = await app.$api[appMethod.value].get({
                id: props.editCatalogueId,
                locale: 'all',
            })
            catalogue.value = categories[0]
        } catch (e) {
            catalogue.value = initInfoCatalogue()
        } finally {
            isLoading.value = false
        }
    }

    const useIsShowVehicleTypes = computed(() => {
        if (!authStore.isTO1) return false
        return (
            supplierStore.contract.serviceType === contractServiceType.ownTransfer ||
            (supplierStore.contract.serviceType === contractServiceType.ownExcursion &&
                supplierStore.fullContract.transportationType === 'OFF_SITE')
        )
    })

    async function loadVehicleTypes() {
        try {
            const {vehicleTypes} = await app.$api.vehicleType.get({active: true})
            vehicleTypesList.value = vehicleTypes.sort((a, b) => a.name?.localeCompare(b.name))
        } catch (e) {
            vehicleTypesList.value = []
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            if (props.editCatalogueId) {
                await app.$api[appMethod.value].put({
                    id: props.editCatalogueId,
                    contractId: props.contractId,
                    ...catalogue.value,
                })
            } else {
                await app.$api[appMethod.value].post({
                    contractId: props.contractId,
                    ...catalogue.value,
                })
            }
            app.$toast.success(t('success_message.saved_successfully'))
            await supplierStore.loadCatalogue()
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    defineExpose({
        save,
        valid,
    })
</script>
